import { type RefObject, useCallback, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"

import { documentToPayload } from "@/editor-core/document"
import { useBlockAutoReload } from "@/hooks/use-block-auto-reload"
import { postJSON } from "@/lib/http"
import { useTakeoffStore } from "@/stores/takeoff-store"

type AutosaveResponse = {
  ok: boolean
  revision?: number
  savepoint_created?: boolean
  error?: string
  /** Validation failures carry the first model message under `base`. */
  details?: Record<string, string>
  locked_by?: string | null
}

const DEBOUNCE_MS = 1500
const ERROR_RETRY_MS = 10_000

/**
 * Debounced full-document autosave for the lock holder. Watches the store's
 * docSeq; ~1.5s after the last change it POSTs the whole document (rooms +
 * calibrations) with the lock token. pagehide/unmount flush with
 * release: true so the final request saves AND frees the lock atomically.
 * A 409 means the lock was lost — the store flips read-only via onLockLost.
 */
export function useTakeoffAutosave({
  takeoffId,
  enabled,
  lockTokenRef,
  onLockLost,
}: {
  takeoffId: number
  enabled: boolean
  lockTokenRef: RefObject<string | null>
  onLockLost: () => void
}) {
  const docSeq = useTakeoffStore((s) => s.docSeq)
  const saveState = useTakeoffStore((s) => s.saveState)
  const inFlight = useRef(false)

  useBlockAutoReload(enabled && (saveState === "dirty" || saveState === "saving"))

  const flush = useCallback(
    async (opts: { release?: boolean; keepalive?: boolean } = {}) => {
      const token = lockTokenRef.current
      if (!token) return
      const store = useTakeoffStore.getState()
      if (store.readOnly) return
      if (inFlight.current && !opts.release) return
      // Nothing to say: no changes to save and no lock to release.
      if (store.saveState !== "dirty" && !opts.release) return

      inFlight.current = true
      const savedSeq = store.docSeq
      store.markSaving()
      try {
        const { status, data } = await postJSON<AutosaveResponse>(
          `/takeoffs/${takeoffId}/autosave`,
          documentToPayload(
            store.rooms,
            store.surfaces,
            store.addOns,
            store.calibBySheet,
            store.cutPlans,
            opts.release ?? false
          ),
          { keepalive: opts.keepalive ?? false, headers: { "X-Takeoff-Lock-Token": token } }
        )
        if (status === 200 && data?.ok && data.revision != null) {
          useTakeoffStore.getState().markSaved(data.revision, savedSeq)
          if (data.savepoint_created) router.reload({ only: ["savepoints"] })
        } else if (status === 409) {
          // Leave state "error", not "saving": if the user reclaims the lock,
          // the error-retry sweep picks the unsaved change back up.
          useTakeoffStore.getState().markSaveError()
          onLockLost()
          router.reload({ only: ["lock"] })
        } else {
          // Show the server's reason in the status line — a 422 repeats
          // forever until the offending value is fixed, and "Save failed"
          // alone gives the estimator nothing to fix.
          const detail = data?.details && Object.values(data.details)[0]
          useTakeoffStore.getState().markSaveError(detail)
        }
      } catch {
        useTakeoffStore.getState().markSaveError()
      } finally {
        inFlight.current = false
      }
    },
    [takeoffId, lockTokenRef, onLockLost]
  )

  // Debounced save after every domain change.
  useEffect(() => {
    if (!enabled || docSeq === 0) return
    if (useTakeoffStore.getState().saveState !== "dirty") return
    const timer = setTimeout(() => flush(), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [docSeq, enabled, flush])

  // A save that finished while more edits queued up leaves state "dirty" with
  // no new docSeq tick; sweep it up. Errors retry on a slower cadence.
  useEffect(() => {
    if (!enabled) return
    if (saveState === "dirty") {
      const timer = setTimeout(() => flush(), DEBOUNCE_MS)
      return () => clearTimeout(timer)
    }
    if (saveState === "error") {
      const timer = setTimeout(() => flush(), ERROR_RETRY_MS)
      return () => clearTimeout(timer)
    }
  }, [saveState, enabled, flush])

  // Tab hidden → flush (keep the lock: the user may come back).
  // Page unload / SPA navigation away → flush AND release in one request.
  useEffect(() => {
    if (!enabled) return
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush({ keepalive: true })
    }
    const onPageHide = () => flush({ release: true, keepalive: true })
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("pagehide", onPageHide)
    return () => {
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("pagehide", onPageHide)
      flush({ release: true, keepalive: true })
    }
  }, [enabled, flush])

  return { flush }
}
